/*
 * Fast Simon SDK collection pilot.
 *
 * Runs INSTEAD of bundle-collections.js on the collection.fs-sdk template.
 * Never load both on the same page - they both own the product grid and the
 * filter rail and will fight over the DOM.
 *
 * It deliberately reuses the theme's existing custom-element names
 * (product-card / filter-option / filter-bubble) and the <template> blocks from
 * main-collections-templates.liquid, so bundle-collections.css applies with no
 * changes. See FACET_MAP below - the CSS keys off theme filter ids such as
 * filter.v.option.size, so FS facet keys must be mapped onto them.
 */
(function () {
    'use strict';

    const configEl = document.getElementById('fs-sdk-config');
    if (!configEl) return;

    const CONFIG = JSON.parse(configEl.textContent);
    const PRODUCTS_PER_PAGE = CONFIG.productsPerPage || 24;

    // Fast Simon requires productsPerPage to stay identical across every request
    // for a given result set, so it is read once and never recomputed.

    /* Hard cap on how many products a collection will ever render, matching
     * MAX_PRODUCTS_PER_COLLECTION in bundle-collections.js. ?limit= may lower it
     * for testing but never raise it, same as the Storefront implementation. */
    const MAX_PRODUCTS = (() => {
        const configured = parseInt(CONFIG.maxProducts, 10);
        const ceiling = isNaN(configured) || configured <= 0 ? 120 : configured;
        const override = parseInt(new URLSearchParams(window.location.search).get('limit'), 10);
        return !isNaN(override) && override > 0 && override <= ceiling ? override : ceiling;
    })();

    let currentPage = 1;
    let pageCount = 1;
    let totalResults = 0;
    let loading = false;
    let requestId = 0;

    /* ------------------------------------------------------------------ *
     * Facet mapping
     *
     * Fast Simon returns facets as positional arrays keyed by dashboard-defined
     * names. The theme's URL scheme and CSS both key off Shopify-style filter
     * ids. This table is the translation layer, seeded from the section setting
     * so it can be corrected without a deploy once the real facet keys are known.
     * ------------------------------------------------------------------ */

    const FACET_MAP = (CONFIG.facetMap || []).reduce((acc, row) => {
        acc.byFs[row.fs.toLowerCase()] = row;
        acc.byTheme[row.theme] = row;
        return acc;
    }, { byFs: {}, byTheme: {} });

    const unmappedFacets = new Set();

    const FS_FALLBACK_PREFIX = 'filter.fs.';

    /* Unmapped facets are passed through rather than dropped, so a facet added in
     * the FS dashboard degrades to "renders unstyled" instead of "vanishes".
     */
    const fsKeyToThemeId = (fsKey) => {
        const mapped = FACET_MAP.byFs[fsKey.toLowerCase()];
        if (mapped) return mapped.theme;
        if (!unmappedFacets.has(fsKey)) {
            unmappedFacets.add(fsKey);
            console.warn('[fs-sdk] Unmapped Fast Simon facet:', fsKey, '- add it to the Facet Map section setting for a clean URL and themed styling.');
        }
        return FS_FALLBACK_PREFIX + fsKey;
    };

    const themeIdToFsKey = (themeId) => {
        const mapped = FACET_MAP.byTheme[themeId];
        if (mapped) return mapped.fs;
        if (themeId.indexOf(FS_FALLBACK_PREFIX) === 0) return themeId.slice(FS_FALLBACK_PREFIX.length);
        return null;
    };

    /* ------------------------------------------------------------------ *
     * URL state - the theme's existing scheme is preserved verbatim, so
     * existing links, topcard filter_specs values and the VWO config keep working.
     * ------------------------------------------------------------------ */

    const isMobileViewport = () => window.innerWidth <= 769;

    const localeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    const productPath = (handle) => localeRoot() + 'products/' + handle;

    const collectionPath = (handle) => localeRoot() + 'collections/' + handle;

    // Re-roots a path Fast Simon gave us, keeping any query string.
    const localizedUrl = (path) => {
        if (!path) return '#';
        const root = localeRoot();
        if (root === '/') return path;
        // Already localised (e.g. after a re-render) - leave it alone.
        if (path.indexOf(root) === 0) return path;
        return root + String(path).replace(/^\//, '');
    };

    const getParams = () => new URLSearchParams(window.location.search);

    const writeParams = (params) => {
        const search = params.toString();
        history.replaceState(null, null, window.location.pathname + (search ? '?' + search : ''));
    };

    const getFilterParams = () => {
        const out = [];
        getParams().forEach((value, key) => {
            if (key.indexOf('filter.') === 0) out.push([key, value]);
        });
        return out;
    };

    /* Theme URL params -> FS narrowBy pairs.
     * Replaces updateFilterQuery() from bundle-collections.js. */
    const paramsToNarrowBy = () => {
        const narrowBy = [];
        const params = getParams();

        // Price is a range facet: theme uses gte/lte, FS wants "<key>_from_to" = "min-max"
        const gte = params.get('filter.v.price.gte');
        const lte = params.get('filter.v.price.lte');
        if (gte || lte) {
            const priceKey = themeIdToFsKey('filter.v.price') || 'Price';
            narrowBy.push([priceKey + '_from_to', (gte || '0') + '-' + (lte || '999999')]);
        }

        getFilterParams().forEach(([key, value]) => {
            if (key.indexOf('filter.v.price') === 0) return;
            const fsKey = themeIdToFsKey(key);
            if (!fsKey) return;
            // Theme comma-joins multi-select; FS wants one pair per selected value.
            value.split(',').forEach((val) => {
                if (val) narrowBy.push([fsKey, val]);
            });
        });

        return narrowBy;
    };

    const SORT_MAP = {
        'best-selling': 'popularity',
        'title': 'a_to_z',
        'price': 'price_min_to_max',
        'newest': 'creation_date',
        'oldest': 'creation_date_oldest'
    };

    /* Omitting sortBy entirely applies the dashboard default, which is what
     * "Featured" (manual) means here - do not send a value for it. */
    const getSortBy = () => SORT_MAP[getParams().get('sort_by')] || undefined;

    /* ------------------------------------------------------------------ *
     * Custom elements - same tag names as the Storefront implementation so
     * bundle-collections.css applies unchanged.
     * ------------------------------------------------------------------ */

    const productMap = new Map();


    const eventName = (key, fallback) =>
        (window.FastSimonEventName && window.FastSimonEventName[key]) || fallback;

    const imageIdOf = (product) => product.imageID || product.image_id || undefined;
    const isPromoTile = (product) => !!product.promotile;

    const promoTilesFrom = (products) =>
        (products || []).filter(isPromoTile).map((promo) => ({
            id: promo.id,
            name: promo.title,
            link: promo.link,
            thumbnail: promo.image
        }));

    function reportEvent(name, data) {
        if (!window.FastSimonSDK || !window.FastSimonSDK.event) return;
        try {
            window.FastSimonSDK.event({ eventName: name, data: data });
        } catch (e) {
            console.warn('[fs-sdk] event report failed', name, e);
        }
    }

    const formatWithDelimiters = (cents, precision, thousands, decimal) => {
        precision = precision === undefined ? 2 : precision;
        thousands = thousands === undefined ? ',' : thousands;
        decimal = decimal === undefined ? '.' : decimal;
        if (isNaN(cents) || cents === null) return '0';

        const fixed = (cents / 100.0).toFixed(precision);
        const parts = fixed.split('.');
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        return whole + (parts[1] ? decimal + parts[1] : '');
    };

    let marketRate = 1;

    async function resolveMarketRate() {
        const source = document.querySelector('[data-col-first-product]');
        if (!source) return;

        const productId = source.getAttribute('data-col-first-product');
        const localeCode = source.getAttribute('data-localization-code') || 'US';
        if (!productId || localeCode === 'US') return;

        const query = `query GetProductById($id: ID!) {
            product(id: $id) {
                priceRangeV2 { maxVariantPrice { amount } }
                contextualPricing(context: {country: ${localeCode}}) {
                    maxVariantPricing { price { amount } }
                }
            }
        }`;

        try {
            const response = await fetch('/community/api/graphql', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ query: query, variables: { id: 'gid://shopify/Product/' + productId } })
            });
            if (!response.ok) return;

            const data = await response.json();
            const base = parseFloat(data?.data?.product?.priceRangeV2?.maxVariantPrice?.amount || 0);
            const local = parseFloat(data?.data?.product?.contextualPricing?.maxVariantPricing?.price?.amount || 0);

            if (base && local) {
                marketRate = local / base;
            }
        } catch (e) {
            console.warn('[fs-sdk] market rate lookup failed - prices stay in base currency', e);
        }
    }

    // Kicked off now, not at onReady, so it resolves during the SDK bootstrap.
    const marketRatePromise = resolveMarketRate();

    const fmtMoney = (amount) => {
        const value = parseFloat(amount);
        if (isNaN(value)) return null;

        const format = (window.globalVariables && window.globalVariables.money_format) || '${{ amount }}';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholder);
        if (!match) return format;

        const cents = value * 100;
        let formatted;
        switch (match[1]) {
            case 'amount_no_decimals':
                formatted = formatWithDelimiters(cents, 0);
                break;
            case 'amount_with_comma_separator':
                formatted = formatWithDelimiters(cents, 2, '.', ',');
                break;
            case 'amount_no_decimals_with_comma_separator':
                formatted = formatWithDelimiters(cents, 0, '.', ',');
                break;
            default:
                formatted = formatWithDelimiters(cents, 2);
        }
        return format.replace(placeholder, formatted);
    };

    const money = (product) => {
        // Same market conversion as renderCardPrice, for the no-offer path.
        const format = (amount) => {
            const value = parseFloat(amount);
            return isNaN(value) ? null : fmtMoney(value * marketRate);
        };

        const min = format(product.p_min);
        const max = format(product.p_max);
        if (min && max && product.p_min !== product.p_max) return min + ' - ' + max;

        return format(product.p) || min || '';
    };

    const fsTags = (product) => attrValues(product, 'Tag');

    const inventoryById = new Map();
    const fsInventory = (product) => {
        const known = inventoryById.get(String(product.id));
        // Undefined (not yet loaded) must not read as 0, which would trip LOW STOCK.
        return known === undefined ? Infinity : known;
    };

    // The original compares against a Shopify ISO date; c_date is unix seconds.
    const fsPublishedAt = (product) =>
        product.c_date ? new Date(product.c_date * 1000).toISOString() : new Date().toISOString();

    /* Rebuild Shopify's options shape ([{name, values:[]}]) from vra so the
     * colour lookup inside the offer table keeps working unchanged. */
    const fsOptions = (product) => {
        const collected = new Map();
        (product.vra || []).forEach((variant) => {
            (variant[1] || []).forEach(([name, values]) => {
                if (!collected.has(name)) collected.set(name, new Set());
                (values || []).forEach((v) => collected.get(name).add(v));
            });
        });
        return Array.from(collected, ([name, values]) => ({ name, values: Array.from(values) }));
    };

    const getExtraLabelText = (key, fallback) => {
        const el = document.getElementById('extra-label-settings');
        if (!el) return fallback;
        try {
            const parsed = JSON.parse(el.textContent);
            return parsed[key] !== undefined ? parsed[key] : fallback;
        } catch (e) {
            return fallback;
        }
    };

    function getOfferSettings(productDetails, priceValue) {
        const productTags = fsTags(productDetails)
        let offerApplied = true;
        let offerLabelColor = '#fff';
        let offerLabelBackground = '#000';
        let offerLabelColorExtra = '#fff';
        let offerLabelBackgroundExtra = '#a0ec96';
        let offerStrokeColor = '#000';
        let offerPrice = priceValue;
        let offerPrice_ab = priceValue;
        let originalPriceB2B = priceValue;
        let offerPrice_vip = priceValue;
        let displayedPrice = '';
        let offerLabel = '';
        let offerLabelAB = '';
        let priceOfferLabel = '';
        let extraLabel = '';
        let badgeClass = '';
        const lowStockLabel = getExtraLabelText('lowStockLabel', 'LOW STOCK');
        const newColorsLabel = getExtraLabelText('newColorsLabel', 'NEW COLORS');
        const lowStockLimit = getExtraLabelText('lowStockLimit', '30');
        const bestSellerLabel = getExtraLabelText('bestSellerLabel', 'BEST SELLER');
        const secondaryBadgeColor = getExtraLabelText('badgeColor', '#fffff');
        const secondaryBadgeBgColor = getExtraLabelText('badgeBgColor', '#C9CDCC');
        const enableSecondaryBadge = getExtraLabelText('enabledExtraLabel', false);

        if (productTags.includes('DISCOUNT30_5') && productTags.includes('WSINGLES')) {
            offerLabel = '30% OFF';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.80;
            originalPriceB2B = priceValue * 2;
            displayedPrice = '<span class="b2b-offer-helper-wrapper"><span class="b2b_price strike-price">{price_b2b}</span><span class="normal_price offer-price">{price}</span></span> <span class="d-flex flex-column col_normal_offer_content">\n<span class="new_offer_design offer-tagline d-flex align-items-center">{price} | <span class="offer-tagline">{offer_price} <span class="hp_only_tag">20% off 3+</span></span></span></span>';
        } else if (['ws-sleep_ss_tshirt', 'ws-sleep_ls_tshirt', 'ws-sleep_shorts', 'ws-sleep_joggers', 'ws-sleep_baller', 'ws-sleep_tank', 'ws-sleep_loose_pants'].some(tag => productTags.includes(tag))) {
            offerLabel = '10% off 2+';
            offerLabelAB = offerLabel;
            badgeClass = 'sleep_badge';
            offerPrice = priceValue * 0.90;
            displayedPrice = '<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design offer-tagline d-flex align-items-center\">{price} <span class=\"offer-tagline\">{offer_price}<div class=\"black-offer-line text-black offer-label-text-change-ab-test-v2 sleep_badge_new\">10% OFF 2+</div></span></span></span>';
        } else if (productTags.includes('WSINGLES') && productTags.includes('WIN_ACTIVE-2026_RELEASE')) {
            offerLabel = '10% off 2+';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.90;
            displayedPrice = '<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design offer-tagline d-flex align-items-center\">{price} <span class=\"offer-tagline\">{offer_price}<div class=\"black-offer-line text-black offer-label-text-change-ab-test-v2 win_product\">10% OFF 2+</div></span></span></span>';
        } else if (productTags.includes('WIN_ACTIVE-2026_RELEASE')) {
            offerLabel = '10% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.90;
            badgeClass = 'sleep_badge'
            displayedPrice = '<span class="d-flex flex-column">\n<span class="new_offer_design offer-tagline d-flex align-items-center">{strike_price} <span class="offer-tagline">{offer_price}</span></span><div class="black-offer-line text-black">Bundle savings</div></span>';
        } else if (productTags.includes('WSINGLES') && productTags.includes('WIN_FLOW_SHORT-2026_RELEASE')) {
            offerLabel = '10% off 2+';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.90;
            displayedPrice = '<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design offer-tagline d-flex align-items-center\">{price} <span class=\"offer-tagline\">{offer_price}<div class=\"black-offer-line text-black offer-label-text-change-ab-test-v2 win_product\">10% OFF 2+</div></span></span></span>';
        } else if (productTags.includes('WSINGLES') && productTags.includes('FLUID-2026_RELEASE')) {
            offerLabel = '10% off 2+';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.90;
            displayedPrice = '<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design offer-tagline d-flex align-items-center\">{price} <span class=\"offer-tagline\">{offer_price}<div class=\"black-offer-line text-black offer-label-text-change-ab-test-v2 win_product\">10% OFF 2+</div></span></span></span>';
        } else if (productTags.includes('FLUID-2026_RELEASE') && productTags.includes('3PACK')) {
            offerLabel = '10% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.90;
            badgeClass = 'fluid_badge'
            displayedPrice = '<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design bundle-pack offer-tagline d-flex align-items-center\">{price} <span class=\"offer-tagline\">{offer_price}<div class=\"black-offer-line text-black offer-label-text-change-ab-test-v2 win_product\">10% OFF</div></span></span></span>';
        }else if (productTags.includes('FLUID-2026_RELEASE') && productTags.includes('5PACK')) {
            offerLabel = '15% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.85;
            badgeClass = 'fluid_badge'
            displayedPrice = '<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design bundle-pack offer-tagline d-flex align-items-center\">{price} <span class=\"offer-tagline\">{offer_price}<div class=\"black-offer-line text-black offer-label-text-change-ab-test-v2 win_product\">15% OFF</div></span></span></span>';
        }else if (productTags.includes('FLUID-2026_RELEASE') && productTags.includes('7PACK')) {
            offerLabel = '20% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.80;
            badgeClass = 'fluid_badge'
            displayedPrice = '<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design bundle-pack offer-tagline d-flex align-items-center\">{price} <span class=\"offer-tagline\">{offer_price}<div class=\"black-offer-line text-black offer-label-text-change-ab-test-v2 win_product\">20% OFF</div></span></span></span>';
        } else if (productTags.includes('SWIM_SINGLE') || (productTags.includes('WSINGLES') && productTags.includes('SWIM_APRIL-2026_RELEASE'))) {
            offerLabel = '10% off 2+';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.90;
            displayedPrice = '<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design offer-tagline d-flex align-items-center\">{price} <span class=\"offer-tagline\">{offer_price}<div class=\"black-offer-line text-black offer-label-text-change-ab-test-v2 win_product\">10% OFF 2+</div></span></span></span>';
        } else if (productTags.includes('SWIM_APRIL-2026_SET')) {
            offerLabel = '10% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.90;
            displayedPrice = '<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design bundle-pack offer-tagline d-flex align-items-center\">{price} <span class=\"offer-tagline\">{offer_price}<div class=\"black-offer-line text-black offer-label-text-change-ab-test-v2 win_product\">10% OFF</div></span></span></span>';
        } else if (productTags.includes('WSINGLES')) {
            offerLabel = '15% OFF 3+';
            offerLabelAB = '10% OFF 3+';
            offerPrice = priceValue * 0.85;
            offerPrice_ab = priceValue * 0.90;
            originalPriceB2B = priceValue * 2;
            displayedPrice = '<span class="b2b-offer-helper-wrapper"><span class="b2b_price strike-price">{price_b2b}</span><span class="normal_price offer-price">{price}</span></span> <span class="d-flex flex-column col_normal_offer_content">\n<span class="new_offer_design offer-tagline d-flex align-items-center">{price} <span class="offer-tagline"><span class="reg_offer">{offer_price}</span><span class="ab_offer">{offer_price_ab}</span><div class="black-offer-line text-black"><span class="reg_offer">15% OFF 3+</span><span class="ab_offer">10% OFF 3+</span></div></span></span></span>';
        } else if (productTags.includes('SLEEP_SET_LONG')) {
            offerLabel = '10% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.90;
            badgeClass = 'sleep_badge'
            displayedPrice = '<span class="d-flex flex-column">\n<span class="new_offer_design offer-tagline d-flex align-items-center">{strike_price} <span class="offer-tagline">{offer_price}</span></span><div class="black-offer-line text-black">Bundle savings</div></span>';
        } else if (productTags.includes('SLEEP_SET_SHORT')) {
            offerLabel = '10% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.90;
            badgeClass = 'sleep_badge'
            displayedPrice = '<span class="d-flex flex-column">\n<span class="new_offer_design offer-tagline d-flex align-items-center">{strike_price} <span class="offer-tagline">{offer_price}</span></span><div class="black-offer-line text-black">Bundle savings</div></span>';
        } else if (productTags.includes('4PACK')) {
            offerLabel = '11% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.89;
            displayedPrice = "<span>{strike_price}</span><span class=\"offer-tagline\">{offer_price} <span class=\"hp_only_tag\">11% off</span></span>";
        } else if (productTags.includes('3PACK')) {
            offerLabel = '15% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.85;
            offerPrice_ab = priceValue * 0.90;
            displayedPrice = `<span class="d-flex flex-column">
                                <span class="new_offer_design offer-tagline d-flex flex-column" style="gap: 0 !important;">
                                    <span>{strike_price}</span>
                                    <div class="d-flex align-items-center" style="gap:6px">
                                        <div><span class="offer-tagline"><span class="reg_offer">{offer_price}</span><span class="ab_offer">{offer_price_ab}</span></span></div>
                                        <div class="black-offer-line text-black offer-label-text-change-ab-test-v2" style="min-width: unset !important;"><span class="reg_offer">15% OFF</span><span class="ab_offer">10% OFF</span></div>
                                    </div>
                                </span>
                            </span>`;
        } else if (productTags.includes('5PACK')) {
            offerLabel = '20% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.80;
            offerPrice_ab = priceValue * 0.85;
            displayedPrice = `<span class="d-flex flex-column">
                                <span class="new_offer_design offer-tagline d-flex flex-column" style="gap: 0 !important;">
                                    <span>{strike_price}</span>
                                    <div class="d-flex align-items-center" style="gap:6px">
                                        <div><span class="offer-tagline"><span class="reg_offer">{offer_price}</span><span class="ab_offer">{offer_price_ab}</span></span></div>
                                        <div class="black-offer-line text-black offer-label-text-change-ab-test-v2" style="min-width: unset !important;"><span class="reg_offer">20% OFF</span><span class="ab_offer">15% OFF</span></div>
                                    </div>
                                </span>
                            </span>`;
        } else if (productTags.includes('7PACK')) {
            offerLabel = '25% off';
            offerLabelAB = offerLabel;
            offerPrice = priceValue * 0.75;
            offerPrice_ab = priceValue * 0.80;
            displayedPrice = `<span class="d-flex flex-column">
                                <span class="new_offer_design offer-tagline d-flex flex-column" style="gap: 0 !important;">
                                    <span>{strike_price}</span>
                                    <div class="d-flex align-items-center" style="gap:6px">
                                        <div><span class="offer-tagline"><span class="reg_offer">{offer_price}</span><span class="ab_offer">{offer_price_ab}</span></span></div>
                                        <div class="black-offer-line text-black offer-label-text-change-ab-test-v2" style="min-width: unset !important;"><span class="reg_offer">25% OFF</span><span class="ab_offer">20% OFF</span></div>
                                    </div>
                                </span>
                            </span>`;
        } else if (['ws-sleep_ss_tshirt', 'ws-sleep_ls_tshirt', 'ws-sleep_shorts', 'ws-sleep_joggers'].some(tag => productTags.includes(tag))) {
            offerLabel = '10% off 2+';
            offerLabelAB = offerLabel;
            badgeClass = 'sleep_badge'
            displayedPrice = "<span class=\"d-flex flex-column\"><span class=\"d-flex flex-column\">\n<span class=\"new_offer_design offer-tagline d-flex align-items-center\">{price}</span></span><span class=\"sleep_offer_tagline\">Bundle & save 10%</span></span>";
        } else if (['GIFTCARD'].some(tag => productTags.includes(tag))) {
            displayedPrice = "<span class=\"d-flex flex-column\">\n<span class=\"new_offer_design offer-tagline d-flex align-items-center\">{price} - {end_price}</span></span>";
        } else {
            displayedPrice = "<span class=\"b2b-offer-helper-wrapper\"><span class=\"b2b_price strike-price\">{price_b2b}</span><span class=\"normal_price offer-price\">{price}</span></span> <span class=\"d-flex flex-column col_normal_offer_content\">\n<span class=\"new_offer_design offer-tagline d-flex align-items-center\">{price} |<span class=\"text-green fw-normal subscriber-text ms-1\">{subscription_price} subscribe</span></span></span>";
        }

        if (enableSecondaryBadge === 'true' || enableSecondaryBadge === true) {
            let labelTag = productTags.find(tag => tag.includes("label-"));
            let colorValue = fsOptions(productDetails).find(opt => opt.name === "Color")?.values[0]?.trim() || '';
            let totalInventory = fsInventory(productDetails);
            const bestSellerColors = ["Black 2.0"];
            const publishedAt = fsPublishedAt(productDetails);
            let isWithinLastTwoWeeks = false;
            if (publishedAt) {
                const diffMs = new Date() - new Date(publishedAt);
                const twoWeeksMs = 14 * 24 * 60 * 60 * 1000;
                isWithinLastTwoWeeks = diffMs <= twoWeeksMs && diffMs >= 0;
            }

            if (totalInventory < parseInt(lowStockLimit) && !productTags.includes('PACK') && !productTags.includes('GIFTCARD')) {
                extraLabel = lowStockLabel;
                offerLabelColorExtra = attrValue(productDetails, 'custom.extra_label_color') || secondaryBadgeColor;
                offerLabelBackgroundExtra = attrValue(productDetails, 'custom.extra_label_background') || secondaryBadgeBgColor;
            }

            if (isWithinLastTwoWeeks) {
                extraLabel = newColorsLabel;
                offerLabelColorExtra = attrValue(productDetails, 'custom.extra_label_color') || secondaryBadgeColor;
                offerLabelBackgroundExtra = attrValue(productDetails, 'custom.extra_label_background') || secondaryBadgeBgColor;

                if (productTags.includes('EXTRAGALACTIC-2026_RELEASE')) {
                    extraLabel = 'RESTOCK';
                }
            }

            if (labelTag) {
                extraLabel = labelTag.replace("label-", "").trim();
                offerLabelColorExtra = attrValue(productDetails, 'custom.extra_label_color') || secondaryBadgeColor;
                offerLabelBackgroundExtra = attrValue(productDetails, 'custom.extra_label_background') || secondaryBadgeBgColor;
            }

            if (bestSellerColors.includes(colorValue)) {
                extraLabel = bestSellerLabel;
                offerLabelColorExtra = attrValue(productDetails, 'custom.extra_label_color') || secondaryBadgeColor;
                offerLabelBackgroundExtra = attrValue(productDetails, 'custom.extra_label_background') || secondaryBadgeBgColor;
            }

            if (productTags.includes('PADLINER_RELEASE')) {
                extraLabel = 'PAD LINER';
                offerLabelColorExtra = attrValue(productDetails, 'custom.extra_label_color') || secondaryBadgeColor;
                offerLabelBackgroundExtra = attrValue(productDetails, 'custom.extra_label_background') || secondaryBadgeBgColor;
            }

            if (productTags.includes('ws-win_baller')) {
                extraLabel = 'FLOATING GUSSET';
                offerLabelColorExtra = attrValue(productDetails, 'custom.extra_label_color') || secondaryBadgeColor;
                offerLabelBackgroundExtra = attrValue(productDetails, 'custom.extra_label_background') || secondaryBadgeBgColor;
            }

            if (productTags.includes('FLUID-2026_RELEASE')) {
                extraLabel = 'PERIOD PROTECTION';
                offerLabelColorExtra = attrValue(productDetails, 'custom.extra_label_color') || secondaryBadgeColor;
                offerLabelBackgroundExtra = attrValue(productDetails, 'custom.extra_label_background') || secondaryBadgeBgColor;
            }
        }

        return {
            offerApplied,
            offerPrice,
            offerPrice_ab,
            displayedPrice,
            offerLabel,
            offerLabelAB,
            priceOfferLabel,
            offerLabelColor,
            offerLabelBackground,
            offerStrokeColor,
            badgeClass,
            offerPrice_vip,
            originalPriceB2B,
            extraLabel,
            offerLabelColorExtra,
            offerLabelBackgroundExtra
        };
    }

      function renderCardPrice(product, priceWrapper, priceValue, cardContent) {
        priceValue = (parseFloat(priceValue) || 0) * marketRate;

        const offer = getOfferSettings(product, priceValue);
        let displayed = offer.displayedPrice;

        // No rule matched - fall back to the plain price.
        if (!displayed) {
            priceWrapper.innerText = money(product);
            return offer;
        }

        const wrap = (cls, amount) => `<span class="${cls}">${fmtMoney(amount)}</span>`;

        displayed = displayed
            .replaceAll('{price}', wrap('regular-price', priceValue))
            .replaceAll('{end_price}', wrap('regular-price', 150.0))
            .replaceAll('{price_b2b}', wrap('regular-price', offer.originalPriceB2B))
            .replaceAll('{offer_price}', wrap('sale-price', offer.offerPrice))
            .replaceAll('{offer_price_ab}', wrap('sale-price', offer.offerPrice_ab))
            .replaceAll('{offer_discount}', '30')
            .replaceAll('{strike_price}', wrap('crossed-price', priceValue))
            // Selling plans are not in the Fast Simon payload, so the
            // subscription placeholder is stripped rather than left visible.
            .replaceAll('{subscription_price}', '')
            .replaceAll('Subscribers', '');

        priceWrapper.innerHTML = displayed;

        // Markets where the offer badge is suppressed, as in the original.
        const suppressed = ['CA', 'GB', 'AU', 'NZ', 'AT'];
        const country = (window.Shopify && window.Shopify.country) || '';
        if (!offer.offerApplied || suppressed.indexOf(country) !== -1) return offer;

        if (offer.priceOfferLabel && offer.priceOfferLabel.length) {
            const badge = document.createElement('span');
            badge.classList.add('badge', 'offer-badge', 'bottom');
            badge.innerHTML = offer.priceOfferLabel;
            badge.style.color = offer.offerLabelColor;
            badge.style.backgroundColor = offer.offerLabelBackground;
            badge.style.borderColor = offer.offerStrokeColor;
            priceWrapper.append(badge);
        }

        const labels = cardContent && cardContent.querySelector('.product-card__labels');
        if (labels) {
            labels.innerHTML = offer.extraLabel || '';
            labels.style.color = offer.offerLabelColorExtra;
            labels.style.backgroundColor = offer.offerLabelBackgroundExtra;
            labels.style.borderColor = offer.offerLabelBackgroundExtra;
        }

        return offer;
    }

    const NAME_MAP = {
        'Baller': '5" Boxer Brief',
        'Star': '3" Boxer Brief',
        'Biker': '9" Boxer Brief',
        'Baller High Waisted': '5" High Waisted',
        'Biker High Waisted': '9" High Waisted',
        'Baller Fly 5"': '5" Boxer Brief w/ Fly',
        'Baller Flex': '5" Active Boxer Brief',
        'Baller Fly 7"': '7" Boxer Brief w/ Fly',
        'Bold': 'Boyshorts',
        'Classic': 'Briefs',
        'Dreamer': 'Boxer w/ Pockets',
        'Baller Pad Liner': '5" Boxer w/ Pad Liner',
        'Baller Luxe': '5" Luxe Boxer Brief',
        'Baller Rib': '5" Ribbed Boxer Brief',
        'Bold Luxe': 'Luxe Boyshorts'
    };

    const attrValues = (product, name) => {
        const wanted = name.toLowerCase();
        const match = (product.att || []).find((entry) => String(entry[0]).toLowerCase() === wanted);
        if (!match) return [];
        return (match[1] || []).map((v) => (Array.isArray(v) ? v[0] : v)).filter(Boolean);
    };

    const attrValue = (product, name) => attrValues(product, name)[0] || '';

    const variantOption = (product, optionName) => {
        const wanted = optionName.toLowerCase();
        for (const variant of product.vra || []) {
            const options = variant[1] || [];
            const match = options.find((opt) => String(opt[0]).toLowerCase() === wanted);
            if (match && match[1] && match[1].length) return String(match[1][0]);
        }
        return '';
    };

    const productColor = (product) =>
        variantOption(product, 'Color') || attrValue(product, 'Color');

    const colorlessTitle = (product, title) => {
        const color = productColor(product);
        if (!color) return title;
        const stripped = title.replace(new RegExp(color.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'), '').trim();
        return stripped || title;
    };

    const STAR_OFFSETS = [0, 19.692307692307693, 39.38461538461539, 59.07692307692308, 78.76923076923077];

    const starRow = (symbol) =>
        `<svg height="14" viewBox="0 0 94.76923076923077 16" aria-hidden="true" role="none">` +
        STAR_OFFSETS.map((x) => `<use x="${x}" href="#${symbol}"></use>`).join('') +
        `</svg>`;

    let warnedMissingRating = false;

    function ratingFrom(product) {
        const json = attrValue(product, 'reviews.rating') || attrValue(product, 'okendo.rating');
        const count = parseInt(
            attrValue(product, 'reviews.rating_count') || attrValue(product, 'okendo.rating_count'),
            10
        );

        if (json) {
            try {
                const parsed = JSON.parse(json);
                const value = parseFloat(parsed.value || 0);
                const scaleMax = parseFloat(parsed.scale_max || 5.0) || 5.0;
                return { percent: (value * 100) / scaleMax, count: isNaN(count) ? 0 : count, fromMetafield: true };
            } catch (e) {
                // fall through to the Fast Simon score
            }
        }

        if (!warnedMissingRating) {
            warnedMissingRating = true;
            console.warn('[fs-sdk] reviews.rating / okendo.rating are not indexed by Fast Simon - falling back to the review score.');
        }

        return {
            percent: parseFloat(product.review),
            count: isNaN(count) ? (parseInt(product.reviews_count, 10) || 0) : count,
            fromMetafield: false
        };
    }

    function renderStars(product, wrapper) {
        if (!wrapper) return;

        const { percent: ratingPercent, count: ratingCount } = ratingFrom(product);

        if ((isNaN(ratingPercent) || ratingPercent === 0) && ratingCount > 0) {
            wrapper.setAttribute('data-oke-star-rating', '');
            wrapper.setAttribute('data-oke-reviews-product-id', 'shopify-' + String(product.id));
            if (window.okeWidgetApi) window.okeWidgetApi.initAllWidgets();
            return;
        }

        // Nothing to show - fall back to the Okendo snippet, else clear the skeleton.
        if (isNaN(ratingPercent) || (ratingPercent === 0 && ratingCount === 0)) {
            wrapper.innerHTML = attrValue(product, 'okendo.StarRatingSnippet') || '';
            return;
        }

        const formattedCount = ratingCount >= 1000
            ? Math.floor(ratingCount / 1000) + ',' + (ratingCount % 1000).toString().padStart(3, '0')
            : ratingCount;

        wrapper.innerHTML = `
            <div class="custom-plp-rating">
                <svg id="oke-star-symbols" class="visually-hidden" style="display:none!important" data-oke-id="oke-star-symbols">
                    <symbol id="oke-star-filled-sym" style="overflow:visible;">
                        <path fill="#000" stroke="#000" stroke-width="1" d="M9.944 6.4L8 0L6.056 6.4H0L4.944 9.928L3.064 16L8 12.248L12.944 16L11.064 9.928L16 6.4H9.944Z"></path>
                    </symbol>
                    <symbol id="oke-star-empty-sym" style="overflow:visible;">
                        <path fill="#fff" stroke="#000" stroke-width="1" d="M9.944 6.4L8 0L6.056 6.4H0L4.944 9.928L3.064 16L8 12.248L12.944 16L11.064 9.928L16 6.4H9.944Z"></path>
                    </symbol>
                </svg>
                <div class="star-container">
                    <div class="oke-stars-background">${starRow('oke-star-empty-sym')}</div>
                    <div class="oke-stars-foreground" style="width: ${ratingPercent}%">${starRow('oke-star-filled-sym')}</div>
                </div>
                <span class="rating-count">(${formattedCount})</span>
            </div>
        `;
    }

    const wishlistEnabled = () =>
        !!(window.globalVariables && window.globalVariables.settings && window.globalVariables.settings.wishlist_setting_enabled);

    const handleFromUrl = (url) => (url || '').split('?')[0].split('/').filter(Boolean).pop() || '';

    function applyWishlist(root, product, rehydrate) {
        if (!wishlistEnabled() || !root) return;

        const button = root.querySelector('[data-custom-wl-epi-btn] .swym-button');
        if (!button) return;

        const productId = String(product.id);
        const variantId = (product.s || '').split('::')[1] || '';

        button.setAttribute('data-product-id', productId);
        if (variantId) button.setAttribute('data-variant-id', variantId);
        button.setAttribute('data-product-url', window.location.origin + productPath(handleFromUrl(product.u)));

        // Drop the template's placeholder product_<id> before adding this one.
        Array.from(button.classList).forEach((cls) => {
            if (cls.indexOf('product_') === 0) button.classList.remove(cls);
        });
        button.classList.add('product_' + productId);

      if (rehydrate) {
            document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
        }
    }

    /* Swym hydrates the hearts when this fires; the wrapper ships `d-none` and is
     * revealed here so half-built buttons never flash. The click guard stops the
     * heart from bubbling into the card link and navigating to the PDP. */
    function activateWishlistButtons() {
        if (!wishlistEnabled()) return;

        document.querySelectorAll('[data-custom-wl-epi-btn]').forEach((el) => {
            el.classList.remove('d-none');
            if (el.dataset.wlBound) return;
            el.dataset.wlBound = 'true';
            el.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                event.stopImmediatePropagation();
            });
        });

        document.dispatchEvent(new CustomEvent('swym:collections-loaded'));
    }

    const CARD_IMAGE_WIDTH = 460;
    const EAGER_IMAGE_COUNT = 4;

    const sizedImage = (url, width) => {
        if (!url) return '';
        return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    };

    function applyCardImage(image, product, position) {
        const src = product.t || product.t2 || '';
        if (!image || !src) return;

        image.style.backgroundColor = '#e3e2e2';
        image.addEventListener('load', () => { image.style.backgroundColor = ''; }, { once: true });

        image.setAttribute('src', sizedImage(src, CARD_IMAGE_WIDTH));
        image.setAttribute('srcset',
            sizedImage(src, CARD_IMAGE_WIDTH) + ' 1x, ' +
            sizedImage(src, CARD_IMAGE_WIDTH * 2) + ' 2x');

        if (position >= 1 && position <= EAGER_IMAGE_COUNT) {
            image.setAttribute('loading', 'eager');
            image.setAttribute('fetchpriority', 'high');
        } else {
            image.setAttribute('loading', 'lazy');
            image.setAttribute('fetchpriority', 'low');
        }
        // Intrinsic ratio is already set on the template (600x600) - leave it.
    }

    class FSProductCard extends HTMLElement {
        connectedCallback() {
            const template = document.getElementById('product-card-template');
            const content = template.content.cloneNode(true);
            const product = productMap.get(this.getAttribute('product-id'));
            if (!product) return;

            /* 1-based. Declared up here because it decides which images load
             * eagerly, as well as being reported with impressions and clicks. */
            const position = parseInt(this.getAttribute('product-position'), 10);
            const title = product.l || '';

            content.querySelectorAll('.product-card__url').forEach((anchor) => {
                anchor.setAttribute('href', localizedUrl(product.u));
            });

            const image = content.querySelector('.product-card__first-image');
            applyCardImage(image, product, position);
            image.setAttribute('alt', title);

            const colorless = colorlessTitle(product, title);

            content.querySelector('.product-card__title').innerText = title;
            content.querySelector('.product-card__title_colorless').innerText = colorless;
            // Offer rules key off the single-unit price, as the original does.
            renderCardPrice(product, content.querySelector('.product-card__price'), parseFloat(product.p) || 0, content);

            /* Descriptive name variant, e.g. Baller -> 5" Boxer Brief.*/
            const abTitle = content.querySelector('.product-card__title_colorless.ab-test');
            if (abTitle) {
                const base = colorless.replace(/\b\d+-Pack\b/gi, '').trim();
                abTitle.innerText = NAME_MAP[base] || colorless;
            }

            // custom.woxer_program is a metafield, available once withAttributes is on.
            const program = content.querySelector('.product-card__title_woxerprogram');
            if (program) program.innerText = attrValue(product, 'custom.woxer_program').toUpperCase();

            const quickAdd = content.querySelector('.product-card__atc_btn.plp_quick_add');
            if (quickAdd) quickAdd.setAttribute('data-product-id', product.id);

            /* Clear the loading skeleton; renderSwatches() fills this in once the
             * per-style sibling lookups resolve. */
            const swatchWrapper = content.querySelector('.product-card__swatches');
            if (swatchWrapper) {
                swatchWrapper.innerHTML = '';
                swatchWrapper.setAttribute('data-selected-id', String(product.id));
            }

            renderStars(product, content.querySelector('.okendo-star-rating'));
            applyWishlist(content, product);

            content.querySelector('.product-card__media-action')?.addEventListener('click', () => {
                this.querySelector('.product-card__url')?.click();
            });

            this.appendChild(content);

            /* Position is 1-based: the SDK rejects anything below 1 with
             * "position must be a positive integer" and drops the impression. */
            if (window.FastSimonSDK && window.FastSimonSDK.trackViewportImpression && position >= 1) {
                window.FastSimonSDK.trackViewportImpression(this, String(product.id), position);
            }

            this.addEventListener('click', () => {
                const selectedId = this.querySelector('.product-card__swatches')?.getAttribute('data-selected-id');
                const clicked = productMap.get(selectedId || String(product.id)) || product;

                reportEvent(eventName('CollectionProductClicked', 'productFromCollection'), {
                    collectionID: String(activeCategoryId()),
                    productID: String(clicked.id),
                    position: position,
                    imageID: imageIdOf(clicked)
                });
            });
        }
    }

    class FSFilterOption extends HTMLElement {
        connectedCallback() {
            const template = document.getElementById('filter-option-template');
            const content = template.content.cloneNode(true);
            const filter = JSON.parse(this.getAttribute('filter-details'));

            this.classList.add('close');

            const label = filter.label === 'Filter Color' ? 'Color' : filter.label;
            content.querySelector('.filter-option__title').innerText = label;
            this.setAttribute('filter-label-value', label.toLowerCase());

            const valuesWrapper = content.querySelector('.filter-option__values');
            if (filter.id.indexOf('color') !== -1) {
                valuesWrapper.classList.add('filter-color-wrapper');
            } else {
                valuesWrapper.classList.add(filter.id.indexOf('size') !== -1 ? 'grid-filter' : 'list-filter');
            }

            const active = (getParams().get(filter.id) || '').split(',');

            filter.values.forEach((value) => {
                const item = buildFilterValue(filter, value, active);
                if (item) valuesWrapper.appendChild(item);
            });

            this.appendChild(content);

            const toggle = this.querySelector('.filter-option__toggle');
            if (toggle) {
                toggle.addEventListener('click', () => {
                    this.classList.toggle('close');
                    if (isMobileViewport()) {
                        const filterValues = this.querySelector('.filter-option__values');
                        filterValues?.scrollIntoView({ behavior: 'smooth' });
                    }
                });
            }
        }
    }

    const readJsonScript = (id) => {
        const el = document.getElementById(id);
        if (!el) return [];
        try {
            return JSON.parse(el.textContent) || [];
        } catch (e) {
            console.warn('[fs-sdk] could not parse', id, e);
            return [];
        }
    };

    const filterColorSettings = readJsonScript('filter-color-settings');
    const filterColorImageSettings = readJsonScript('filter-color-image-settings');

    const FILTER_LABEL_MAP = {
        'Baller Flex': 'Baller 5" Flex Boxers',
        'Biker Flex': 'Biker 9" Flex Boxers',
        'Baller Fly 5"': 'Baller 5" Boxers w/ Fly',
        'Baller Fly 7"': 'Baller 7" Boxers w/ Fly',
        'Biker High Waisted': 'Biker 9" High Waisted Boxers',
        'Boss Flex': 'Boss Flex Sports Bra',
        'Icon 2.0': 'Icon Slim Brallete',
        'Star High Waisted': 'Star 3" High Waisted'
    };

    function paintSwatchCircle(circle, label) {
        const wanted = label.toLowerCase();
        const hex = filterColorSettings.find((c) => (c.colorName || '').toLowerCase() === wanted);
        if (hex && hex.colorHexValue) {
            circle.style.backgroundColor = hex.colorHexValue;
            return;
        }

        const base = document.querySelector('[data-file-url-base]')?.dataset.fileUrlBase;
        if (!base) return;

        const image = filterColorImageSettings.find((c) => (c.colorName || '').toLowerCase() === wanted);
        const fileName = image && image.colorImage
            ? image.colorImage
            : label.toLowerCase().replaceAll(' ', '-');

        const url = (image && image.colorImage
            ? base.replace('multi_small.jpg', fileName)
            : base.replace('multi', fileName)
        ).replace('127.0.0.1:9292', 'woxer.com');

        circle.style.backgroundImage = `url(${url}&width=31)`;
    }

    function buildFilterValue(filter, value, active) {
        const isActive = active.indexOf(value.label) !== -1;
        const item = document.createElement('label');
        item.dataset.filterId = filter.id;
        item.dataset.value = value.label;
        if (isActive) item.classList.add('active');
        if (value.count === 0) item.classList.add('disabled');

        if (filter.label === 'Size') {
            item.classList.add('filter-swatch');
            item.setAttribute('role', 'button');
            item.title = value.label;
            const text = document.createElement('span');
            text.innerText = value.label;
            item.appendChild(text);
            item.addEventListener('click', (event) => {
                event.preventDefault();
                toggleFilterValue(filter.id, value.label);
            });
            return item;
        }

        item.classList.add('filter-checkbox');

        const checkbox = document.createElement('input');
        checkbox.type = 'checkbox';
        checkbox.name = filter.id;
        checkbox.value = value.label;
        checkbox.checked = isActive;

       checkbox.addEventListener('change', () => {
            toggleFilterValue(filter.id, value.label);
        });

        if (filter.id.indexOf('color') !== -1) {
            item.classList.add('filter-color');
            // Shortest labels first, as the Storefront version orders them.
            item.style.order = value.label.length;

            const circle = document.createElement('div');
            circle.classList.add('swatch-circle');
            circle.innerHTML = `<span class="hide">${value.label}</span>`;
            // Prefer a hex supplied by Fast Simon, else fall back to the theme palette.
            if (value.hex) {
                circle.style.backgroundColor = value.hex;
            } else {
                paintSwatchCircle(circle, value.label);
            }
            item.appendChild(circle);
        }

        const text = document.createElement('span');
        text.classList.add('label-text');
        const display = FILTER_LABEL_MAP[value.label] || value.label;
        text.innerText = display;
        item.title = display;

        item.append(checkbox, text);
        return item;
    }

    class FSFilterBubble extends HTMLElement {
        connectedCallback() {
            const template = document.getElementById('filter-bubble-filter');
            const content = template.content.cloneNode(true);
            const paramName = this.getAttribute('data-param-name');
            const value = this.getAttribute('data-label');

            content.querySelector('.filter-bubble-label').innerText = value;
            this.appendChild(content);
            this.addEventListener('click', () => toggleFilterValue(paramName, value));
        }
    }

    if (!customElements.get('product-card')) customElements.define('product-card', FSProductCard);
    if (!customElements.get('filter-option')) customElements.define('filter-option', FSFilterOption);
    if (!customElements.get('filter-bubble')) customElements.define('filter-bubble', FSFilterBubble);

    /* ------------------------------------------------------------------ *
     * Filter interaction
     * ------------------------------------------------------------------ */
    let stagedParams = null;

    const activeParams = () => stagedParams || getParams();

    function markFilterValue(paramName, value, isSelected) {
        document.querySelectorAll('.filter-option__values [data-filter-id]').forEach((item) => {
            if (item.dataset.filterId !== paramName) return;
            if ((item.dataset.value || item.getAttribute('title')) !== value) return;

            item.classList.toggle('active', isSelected);
            const checkbox = item.querySelector('input[type="checkbox"]');
            if (checkbox) checkbox.checked = isSelected;
        });
    }

    function toggleFilterValue(paramName, value) {
        const params = activeParams();
        const current = params.get(paramName);
        let values = current ? current.split(',') : [];

        const index = values.indexOf(value);
        const isSelected = index === -1;
        if (isSelected) {
            values.push(value);
        } else {
            values.splice(index, 1);
        }

        if (values.length) {
            params.set(paramName, values.join(','));
        } else {
            params.delete(paramName);
        }

        // Staging: show the tick, change nothing else. Apply commits.
        if (stagedParams) {
            markFilterValue(paramName, value, isSelected);
            return;
        }

        writeParams(params);
        load({ resetPage: true, keepFacets: true });
    }

    function syncFilterStatus() {
        const params = getParams();

        document.querySelectorAll('.filter-option__values [data-filter-id]').forEach((item) => {
            const filterId = item.dataset.filterId;
            const value = item.dataset.value || item.getAttribute('title');
            const selected = (params.get(filterId) || '').split(',');
            const isActive = !!value && selected.indexOf(value) !== -1;

            item.classList.toggle('active', isActive);
            const checkbox = item.querySelector('input[type="checkbox"]');
            if (checkbox) checkbox.checked = isActive;
        });

        updateFilterCounts();
    }

    function updateFilterCounts() {
        document.querySelectorAll('filter-option').forEach((option) => {
            const title = option.querySelector('.filter-option__title');
            if (!title) return;

            let label;
            try {
                label = JSON.parse(option.getAttribute('filter-details')).label;
            } catch (e) {
                return;
            }
            // Same rename the option applies when it first renders.
            if (label === 'Filter Color') label = 'Color';

            const active = option.querySelectorAll('.filter-option__values .active').length;
            title.innerText = label + (active ? ' (' + active + ')' : '');
        });
    }

    function clearAllFilters() {
        // Clear ends staging too - it closes the drawer, so it has to commit.
        stagedParams = null;

        const params = getParams();
        Array.from(params.keys()).forEach((key) => {
            if (key.indexOf('filter.') === 0) params.delete(key);
        });
        writeParams(params);
        // Same reasoning as toggleFilterValue - the shopper is still in the rail.
        load({ resetPage: true, keepFacets: true });
    }

    /* ------------------------------------------------------------------ *
     * Rendering
     * ------------------------------------------------------------------ */

    const grid = () => document.querySelector('.collection-bundle:not(.d-none)');

    const activeCategoryId = () =>
        grid()?.getAttribute('data-fs-category-id') || CONFIG.categoryID;


    function showSkeletonCards(count) {
        const wrapper = grid();
        const template = document.getElementById('skeleton-card-template');
        const cardMarkup = template && template.content.querySelector('.card.collection-product-card');
        if (!wrapper || !cardMarkup) return;

        removeSkeleton();

        for (let i = 0; i < count; i++) {
            const item = document.createElement('div');
            item.className = 'collection-skeleton infinite_skeleton infinite-scroll-skeleton';
            item.appendChild(cardMarkup.cloneNode(true));
            wrapper.appendChild(item);
        }
    }

    function removeSkeleton() {
        grid()?.querySelectorAll('.infinite_skeleton').forEach((el) => el.remove());
    }

    function renderProducts(products, append) {
        const wrapper = grid();
        if (!wrapper) return;

        if (append) {
            removeSkeleton();
        } else {
            wrapper.querySelectorAll('product-card').forEach((card) => card.remove());
            productMap.clear();
        }

        const offset = wrapper.querySelectorAll('product-card').length;

        const capped = products.slice(0, Math.max(0, MAX_PRODUCTS - offset));

        capped.forEach((product, index) => {
            productMap.set(String(product.id), product);
            const card = document.createElement('product-card');
            card.setAttribute('product-id', String(product.id));
            // 1-based, and continuing across infinite-scroll pages.
            card.setAttribute('product-position', String(offset + index + 1));
            card.setAttribute('data-collection-product-card', '');
            // Matches bundle-collections.js's product-is-pack attribute, which bundle-collections.css keys off.
            card.setAttribute('product-is-pack', isPackProduct(product) ? 'true' : 'false');
            // s is "productId::variantId" - the variant is what Swym and quick-add need.
            const variantId = (product.s || '').split('::')[1];
            if (variantId) card.setAttribute('product-variant-id', variantId);
            wrapper.appendChild(card);
        });

        // The empty-state is driven by [data-products-count="0"] in bundle-collections.css.
        wrapper.setAttribute('data-products-count', String(wrapper.querySelectorAll('product-card').length));
    }

    /* FS facet -> the object shape the filter renderer expects.
     * Facet = [key, values, displayName, isActive, num]
     * FacetValue = [label, count]; ColorFamilyFacetValue = [label, hex, ?] */
    function adaptFacet(facet) {
        const [fsKey, values, displayName] = facet;
        const themeId = fsKeyToThemeId(fsKey);
        const isColor = themeId.indexOf('color') !== -1;

        return {
            id: themeId,
            label: displayName || fsKey,
            type: 'LIST',
            values: (values || []).map((entry) => {
                if (isColor && typeof entry[1] === 'string') {
                    return { label: entry[0], hex: entry[1], count: null };
                }
                return { label: entry[0], count: entry[1] };
            })
        };
    }

    function renderFacets(facets) {
        const wrapper = document.querySelector('.collection-facets__filter-options');
        if (!wrapper) return;

        wrapper.querySelectorAll('filter-option').forEach((el) => el.remove());

        (facets || []).forEach((facet) => {
            /* Range facets arrive as paired _min/_max keys whose values are bare
             * numbers ([14], [150]) rather than [label, count] pairs, so they must
             * be skipped BEFORE adapting or they produce undefined labels. */
            if (/_(min|max)$/.test(facet[0])) return;
            // The parent range facet ("Price") carries no values of its own.
            if (!facet[1] || !facet[1].length) return;

            const adapted = adaptFacet(facet);
            const element = document.createElement('filter-option');
            element.setAttribute('filter-id', adapted.id);
            element.setAttribute('filter-details', JSON.stringify(adapted));
            wrapper.appendChild(element);
        });

        sortFacetsBySettings();
        // A page loaded with filters already in the URL must show its counts.
        updateFilterCounts();
    }

    function syncFacetAvailability(facets) {
        const params = getParams();
        const byThemeId = new Map();

        (facets || []).forEach((facet) => {
            if (/_(min|max)$/.test(facet[0])) return;
            if (!facet[1] || !facet[1].length) return;

            const adapted = adaptFacet(facet);
            const values = new Map();
            adapted.values.forEach((value) => {
                // count === 0 means present but exhausted; treat as unavailable.
                if (value.count !== 0) values.set(value.label, value);
            });
            byThemeId.set(adapted.id, { adapted: adapted, values: values });
        });

        document.querySelectorAll('filter-option').forEach((option) => {
            const filterId = option.getAttribute('filter-id');
            const entry = byThemeId.get(filterId);
            const wrapper = option.querySelector('.filter-option__values');

            // Whole facet gone from the response - hide the group.
            option.classList.toggle('d-none', !entry);
            if (!entry || !wrapper) return;

            const rendered = new Set();
            wrapper.querySelectorAll('[data-filter-id]').forEach((item) => {
                const label = item.dataset.value;
                rendered.add(label);
                item.classList.toggle('disabled', !entry.values.has(label));
            });

            /* Values can become available again when a filter is removed. They
             * may never have been rendered, so append the missing ones. */
            const active = (params.get(filterId) || '').split(',');
            entry.values.forEach((value, label) => {
                if (rendered.has(label)) return;
                const item = buildFilterValue(entry.adapted, value, active);
                if (item) wrapper.appendChild(item);
            });
        });

        updateFilterCounts();
    }

    /* Reuses the theme's existing main_filter_sort / sub_filter_sort settings,
     * which order by DOM label and so survive the backend swap untouched. */
    function sortFacetsBySettings() {
        const order = CONFIG.filterSort || [];
        if (!order.length) return;

        document.querySelectorAll('filter-option').forEach((element) => {
            const label = (element.getAttribute('filter-label-value') || '').toLowerCase();
            const index = order.findIndex((entry) => label.indexOf(entry.toLowerCase()) !== -1);
            element.style.order = index !== -1 ? index + 1 : 20;
        });
    }

    function renderBubbles() {
        document.querySelectorAll('filter-bubble').forEach((el) => el.remove());

        const wrappers = document.querySelectorAll('.collection-facets__filter-bubbles');
        let count = 0;

        getFilterParams().forEach(([key, value]) => {
            if (key.indexOf('filter.v.price') === 0) return;
            value.split(',').forEach((val) => {
                count++;
                wrappers.forEach((wrapper) => {
                    const bubble = document.createElement('filter-bubble');
                    bubble.dataset.label = val;
                    bubble.dataset.paramName = key;
                    wrapper.appendChild(bubble);
                });
            });
        });

        document.querySelectorAll('.filter-clear-all').forEach((btn) => {
            btn.classList.toggle('hide-display', count === 0);
        });
        document.querySelectorAll('.filter-mobile-toggle .applied-filters').forEach((el) => {
            el.innerText = count ? '(' + count + ')' : '';
        });
    }

    function renderCounts() {
        /* Never advertise more than can actually be reached, mirroring the
         * Math.min against MAX_PRODUCTS_PER_COLLECTION in bundle-collections.js. */
        const shown = Math.min(totalResults, MAX_PRODUCTS);

        document.querySelectorAll('.collections-bundles__product-count').forEach((el) => {
            el.innerText = shown + ' ' + (shown === 1 ? 'product' : 'products');
        });

        const loaded = grid() ? grid().querySelectorAll('product-card').length : 0;
        const loadedEl = document.querySelector('[data-loaded-item-number]');
        const totalEl = document.querySelector('[data-total-item-number]');
        if (loadedEl && totalEl) {
            loadedEl.innerText = Math.min(loaded, shown);
            totalEl.innerText = shown;
            const info = loadedEl.closest('.col-pagination-info');
            if (info) info.classList.toggle('hidden', loaded === 0);
        }

        const desktopCount = document.querySelector('.collection-bundle:not(.d-none) .category-pd-counts');
        if (desktopCount) desktopCount.innerText = '(' + shown + ')';
    }

    /* Inventory arrives after the grid paints, so any card whose secondary label
     * depends on stock has to be re-evaluated once it does. */
    function repriceRenderedCards() {
        const wrapper = grid();
        if (!wrapper) return;

        wrapper.querySelectorAll('product-card').forEach((card) => {
            const selected = card.querySelector('.product-card__swatches')?.getAttribute('data-selected-id');
            const product = productMap.get(selected || card.getAttribute('product-id'));
            const priceEl = card.querySelector('.product-card__price');
            if (!product || !priceEl) return;
            priceEl.innerHTML = '';
            renderCardPrice(product, priceEl, parseFloat(product.p) || 0, card);
        });
    }

    function toggleSkeleton(show) {
        document.querySelector('.collection-skeleton')?.classList.toggle('d-none', !show);
        document.querySelector('.collection-product-wrapper')?.classList.toggle('d-none', show);
    }

    /* ------------------------------------------------------------------ *
     * Data load
     * ------------------------------------------------------------------ */

    function load(options) {
        const opts = options || {};
        if (opts.resetPage) currentPage = 1;

        const id = ++requestId;
        let renderedForRequest = false;
        loading = true;

        if (opts.append) {
            showSkeletonCards(PRODUCTS_PER_PAGE);
        } else {
            toggleSkeleton(true);
        }
        renderBubbles();

        if (opts.keepFacets) syncFilterStatus();

        window.FastSimonSDK.smartCollections({
            categoryID: activeCategoryId(),
            page: currentPage,
            productsPerPage: PRODUCTS_PER_PAGE,
            narrowBy: paramsToNarrowBy(),
            sortBy: getSortBy(),
            facets: true,
            withBadges: true,
            withAttributes: CONFIG.withAttributes !== false,

            inventoryCallback: ({ payload }) => {
                (payload?.productsInventory || []).forEach((entry) => {
                    inventoryById.set(String(entry.id), entry.totalInventory);
                });
                repriceRenderedCards();
            },

            callback: ({ action, payload }) => {
                if (id !== requestId) return;

                if (payload.error) {
                    console.error('[fs-sdk] smartCollections failed', payload);
                    if (opts.append) removeSkeleton();
                    toggleSkeleton(false);
                    loading = false;
                    return;
                }

                totalResults = payload.totalResults || 0;
                pageCount = payload.pageCount || 1;

                if (!renderedForRequest) {
                    renderedForRequest = true;
                    renderProducts(payload.products || [], !!opts.append);
                    renderCounts();
                    // Fire and forget - swatches need extra requests and must not
                    // hold up the grid paint.
                    renderSwatches();
                    activateWishlistButtons();

                    const returned = payload.products || [];
                    const productIds = returned.filter((p) => !isPromoTile(p)).map(({ id }) => id).toString();
                    const promoTiles = promoTilesFrom(returned);

                    reportEvent(eventName('SmartCollectionPreformed', 'collection'), {
                        categoryID: String(activeCategoryId()),
                        narrowBy: paramsToNarrowBy(),
                        sortBy: getSortBy(),
                        products: productIds.length ? productIds : undefined,
                        promoTiles: promoTiles.length ? promoTiles : undefined,
                        rescount: payload.totalResults,
                        pagenum: payload.page
                    });
                }

                if (action === 'facets and products') {
                    if (opts.keepFacets) {
                        syncFacetAvailability(payload.facets);
                    } else {
                        renderFacets(payload.facets);
                    }
                }

                toggleSkeleton(false);
                loading = false;
            }
        });
    }

    /* ------------------------------------------------------------------ *
     * Sort + infinite scroll
     * ------------------------------------------------------------------ */

    function bindSorting() {
        document.querySelectorAll('.sort-by-list button, .sort-by-list-filters button').forEach((button) => {
            button.addEventListener('click', () => {
                const value = button.dataset.value;
                const params = getParams();

                if (value === 'manual') {
                    params.delete('sort_by');
                } else {
                    params.set('sort_by', value);
                }
                writeParams(params);

                document.querySelectorAll('.sort-by-list button, .sort-by-list-filters button')
                    .forEach((el) => el.classList.toggle('active', el.dataset.value === value));

                const label = document.querySelector('.collections-bundles__sort-by-trigger span');
                if (label) label.innerText = 'Sort by ' + button.innerText.replace('Radio Button', '').trim();

                load({ resetPage: true });
            });
        });

        // Mobile sort dropdown.
        document.querySelector('.collections-bundles__sort-by-trigger')?.addEventListener('click', () => {
            document.querySelector('.sort-by-list')?.classList.toggle('hide');
        });

        // Desktop sort accordion in the filter rail - same `close` convention.
        document.querySelector('.sort-by-filters__trigger')?.addEventListener('click', function () {
            this.closest('.sort-by-filters')?.classList.toggle('close');
        });
    }

    function bindInfiniteScroll() {
        window.addEventListener('scroll', () => {
            if (loading || currentPage >= pageCount) return;
            // Stop paging once the cap is reached, even if more pages exist.
            if ((grid()?.querySelectorAll('product-card').length || 0) >= MAX_PRODUCTS) return;

            const lastCard = grid()?.querySelector('product-card:last-child');
            if (!lastCard) return;

            const rect = lastCard.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
                currentPage++;
                load({ append: true });
            }
        }, { passive: true });
    }

    /* The drawer state lives on <html> as well as the wrapper - CSS such as
     * `html.open .collection-sticky-menu` keys off it and it locks body scroll. */
    function setDrawerOpen(isOpen) {
        document.querySelector('.collection-facets-wrapper')?.classList.toggle('open', isOpen);
        document.documentElement.classList.toggle('open', isOpen);
    }

    function bindFilterChrome() {
        document.querySelectorAll('.filter-clear-all').forEach((btn) => {
            btn.addEventListener('click', () => {
                clearAllFilters();
                // Clearing everything is decisive enough to close the drawer.
                if (isMobileViewport()) setDrawerOpen(false);
            });
        });

        document.querySelectorAll('.filter-mobile-toggle').forEach((btn) => {
            btn.addEventListener('click', () => {
                // Open with every accordion collapsed, as the live drawer does.
                document
                    .querySelectorAll('.collection-facets__filter-options filter-option, .collection-facets__filter-options .sort-by-filters')
                    .forEach((el) => el.classList.add('close'));
                // Start staging from whatever is currently applied.
                stagedParams = getParams();
                setDrawerOpen(true);
            });
        });

        // Apply is the only thing that commits a mobile selection.
        document.querySelector('.filter-view-products')?.addEventListener('click', () => {
            const params = stagedParams;
            stagedParams = null;
            setDrawerOpen(false);

            if (params) writeParams(params);
            renderBubbles();
            syncFilterStatus();
            load({ resetPage: true, keepFacets: true });
        });

        /* Closing without applying discards the staged selection, so the ticks
         * are reverted to match what is actually applied. */
        document.querySelectorAll('.facets-mobile-close').forEach((btn) => {
            btn.addEventListener('click', () => {
                stagedParams = null;
                setDrawerOpen(false);
                syncFilterStatus();
            });
        });
    }

    /* ------------------------------------------------------------------ *
     * Topcards
     *
     * Topcards are the same metaobject-driven markup as main-collections-scroll.liquid,
     * ported from the fix on 09152026-sleep-style-release (bundle-collections.js) since
     * this file predated it. Three click behaviors, matched by selector:
     *   - [data-plp-new-structure-card].redirect            -> real navigation, just log the click
     *   - [data-plp-new-structure-card]:not(.redirect, [data-topcard-filter]) -> swap the
     *     already-prerendered destination bundle in place, no reload
     *   - [data-topcard-filter]                              -> apply a style/pack filter to the
     *     current collection instead of navigating (swim-collection, pride-collection rows)
     * ------------------------------------------------------------------ */

    function pushTopcardClickEvent(collectionHandle, cardTitle) {
        window.VWO = window.VWO || [];
        window.VWO.event = window.VWO.event || function () { window.VWO.push(['event'].concat([].slice.call(arguments))); };
        window.VWO.event('topCardClick', {
            level1: collectionHandle || '',
            level2: '',
            level3: cardTitle || ''
        });
    }

    const getCurrentCollectionHandle = () =>
        document.querySelector('.collection-header')?.getAttribute('data-collection-handle') || '';

    function setCollectionContext(handle) {
        document.querySelector('.collection-header')?.setAttribute('data-collection-handle', handle);
        document.querySelector('.collection-sticky-menu')?.setAttribute('data-collection-handle', handle);
    }

    function scrollTopcardIntoView(card) {
        const wrapper = card.closest('.swiper-wrapper');
        if (!wrapper) return;
        const wrapperRect = wrapper.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        wrapper.scrollTo({
            left: wrapper.scrollLeft + (cardRect.left - wrapperRect.left) - (wrapperRect.width / 2 - cardRect.width / 2)
        });
    }

    function scrollStickyIntoView(sticky) {
        const container = sticky.closest('ul');
        if (!container) return;
        container.scrollTo({
            left: sticky.offsetLeft - container.clientWidth / 2 + sticky.clientWidth / 2
        });
    }

   function scrollToTopcardHeader() {
        const header = document.querySelector('[data-new-collection-structure]');
        if (!header) return;
        window.scrollTo({ top: header.offsetTop - 80, left: 0 });
    }

    /* Only the swiper drag itself decides isDragging - Swiper's own config here
     * (freeMode, grabCursor) does not suppress the click that follows a drag. */
    let isDragging = false;
    document.querySelectorAll('.collection-menu__items .swiper-slider').forEach((slider) => {
        let downX = 0;
        let downY = 0;
        slider.addEventListener('pointerdown', (event) => {
            isDragging = false;
            downX = event.clientX || 0;
            downY = event.clientY || 0;
        }, { passive: true });
        slider.addEventListener('pointermove', (event) => {
            const dx = Math.abs((event.clientX || 0) - downX);
            const dy = Math.abs((event.clientY || 0) - downY);
            if (dx > 5 || dy > 5) isDragging = true;
        }, { passive: true });
    });

    function swapTopcardBundle(card) {
        const newValue = card.getAttribute('data-plp-new-structure-card');
        if (!newValue) return;
        const handle = newValue.replace('collection-', '');

        document.querySelectorAll('[data-plp-new-structure-products]').forEach((el) => el.classList.add('d-none'));
        document.querySelector('[data-plp-new-structure-products="' + newValue + '"]')?.classList.remove('d-none');

        pushTopcardClickEvent(getCurrentCollectionHandle(), card.getAttribute('data-card-title'));

        history.replaceState(null, null, window.location.origin + collectionPath(handle));
        setCollectionContext(handle);

        // The card pointing at where you just landed shouldn't still invite a click.
        document.querySelectorAll('.collection-menu__items .collection-menu__item').forEach((el) => el.classList.remove('d-none'));
        card.closest('.collection-menu__items .collection-menu__item')?.classList.add('d-none');

        const clickedFromSticky = !!card.closest('.collection-menu__item-sticky');
        const stickyCard = clickedFromSticky
            ? card
            : document.querySelector('.collection-menu__item-sticky [data-plp-new-structure-card="' + newValue + '"]');
        const activeSticky = stickyCard?.closest('.collection-menu__item-sticky');

        if (activeSticky) {
            document.querySelector('.collection-menu__item-sticky.active')?.classList.remove('active');
            activeSticky.classList.add('active');
            scrollStickyIntoView(activeSticky);

            // Sticky is already visible when clicked directly - only re-center the main
            // carousel when the sticky menu was the one that drove the swap.
            if (clickedFromSticky) {
                const mainCard = document.querySelector('.collection-menu__item_v2:has([data-plp-new-structure-card="' + newValue + '"])');
                if (mainCard) scrollTopcardIntoView(mainCard);
            }
        }

        scrollToTopcardHeader();

        // A fresh destination collection, so its URL carries no filters - matches a real navigation.
        load({ resetPage: true });
    }

    /* boxer-briefs/bralettes-and-tops/etc. are style-filter shortcuts within the
     * CURRENT collection (swim-collection, pride-collection rows), not a collection swap. */
    const TOPCARD_FILTER_STYLES = {
        'boxer-briefs': 'Baller,Star,Biker',
        'bralettes-and-tops': 'Boss',
        'bikinis-and-briefs': 'Bikini,Classic',
        'bikinis': 'Bikini',
        'tops': 'Boss',
        'shorts': 'Baller,Star'
    };

    function applyTopcardFilter(card) {
        const filterVal = card.getAttribute('data-topcard-filter');
        pushTopcardClickEvent(getCurrentCollectionHandle(), card.getAttribute('data-card-title'));

        const params = getParams();
        const styleValue = TOPCARD_FILTER_STYLES[filterVal];
        if (styleValue) {
            params.set('filter.p.m.custom.styles', styleValue);
        } else {
            params.delete('filter.p.m.custom.styles');
        }

        if (filterVal === 'packs' || filterVal === 'sets') {
            params.set('filter.p.tag', 'PACK');
        } else {
            params.delete('filter.p.tag');
        }

        writeParams(params);

        document.querySelectorAll('.collection-menu__item-sticky').forEach((item) => item.classList.remove('active'));
        const correspondingSticky = document.querySelector('.collection-menu__item-sticky[data-col-handle="' + filterVal + '"]');
        if (correspondingSticky) {
            correspondingSticky.classList.add('active');
            scrollStickyIntoView(correspondingSticky);
        }

        const clickedFromSticky = !!card.closest('.collection-menu__item-sticky');
        if (clickedFromSticky) {
            const correspondingSlide = document.querySelector('.collection-menu__items .collection-menu__item[data-col-handle="' + filterVal + '"]');
            if (correspondingSlide) scrollTopcardIntoView(correspondingSlide);
        }

        scrollToTopcardHeader();

        load({ resetPage: true, keepFacets: true });
    }

    function bindTopcards() {
        document.querySelectorAll('[data-plp-new-structure-card].redirect').forEach((card) => {
            card.addEventListener('click', () => {
                pushTopcardClickEvent(getCurrentCollectionHandle(), card.getAttribute('data-card-title'));
            });
        });

        document.querySelectorAll('[data-plp-new-structure-card]:not(.redirect, [data-topcard-filter])').forEach((card) => {
            card.addEventListener('click', (event) => {
                if (isDragging) {
                    isDragging = false;
                    return;
                }
                event.preventDefault();
                swapTopcardBundle(card);
            });
        });

        document.querySelectorAll('[data-topcard-filter]').forEach((card) => {
            card.addEventListener('click', (event) => {
                if (isDragging) {
                    isDragging = false;
                    return;
                }
                event.preventDefault();
                applyTopcardFilter(card);
            });
        });

        bindStickyMenu();
    }

    function revealTopcardRows() {
        document
            .querySelectorAll('.collection-menu__items.opacity-0, .collection-sticky-menu ul.opacity-0')
            .forEach((row) => row.classList.remove('opacity-0'));
    }

   (function scheduleTopcardReveal() {
        const reveal = () => requestAnimationFrame(revealTopcardRows);
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', reveal, { once: true });
        } else {
            reveal();
        }
    })();

    function isInViewport(el, percentVisible) {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;
        const header = document.querySelector('sticky-header .site-header');
        const headerHeight = (header ? header.offsetHeight : 0) - 10;

        return !(
            Math.floor(100 - (((rect.top - headerHeight) >= 0 ? 0 : (rect.top - headerHeight)) / -rect.height) * 100) < percentVisible ||
            Math.floor(100 - ((rect.bottom - headerHeight - windowHeight) / rect.height) * 100) < percentVisible ||
            Math.floor(100 - ((rect.left >= 0 ? 0 : rect.left) / -rect.width) * 100) < percentVisible ||
            Math.floor(100 - ((rect.right - windowWidth) / rect.width) * 100) < percentVisible
        );
    }

    function bindStickyMenu() {
        const stickyMenu = document.querySelector('.collection-sticky-menu');
        if (!stickyMenu) return;

        const update = () => {
            const headerVisible = isInViewport(document.querySelector('.collection-header'), 1);
            const bannerVisible = isInViewport(document.querySelector('.collection-hero__image-container'), 1);
            const descriptionVisible = isInViewport(document.querySelector('.collection-bundle:not(.d-none) .category-separator-description'), 1);
            const facets = document.querySelector('.collection-facets-wrapper');
            const siteHeaderNav = document.querySelector('sticky-header nav');

            if (headerVisible || bannerVisible || descriptionVisible) {
                stickyMenu.classList.remove('show');
                facets?.classList.remove('sticky');
                siteHeaderNav?.classList.add('shadow');
            } else {
                stickyMenu.classList.add('show');
                facets?.classList.add('sticky');
                siteHeaderNav?.classList.remove('shadow');
            }
        };

        window.addEventListener('scroll', update, { passive: true });
        update();
    }

    /* ------------------------------------------------------------------ *
     * Colour swatches
     *
     * Singles:
     *   1. collect the distinct Custom.styles values on the page
     *   2. one narrowed query per style (cached; responses are public/300s)
     *   3. bucket the results by the Type attribute, which is the precise
     *      grouping key - Custom.styles alone merges Baller with Baller Flex
     *
     * Packs: bundle-collections.js's equivalent (swatchCollectionHandle,
     * bundle-collections.js:3390-3434) keys off Shopify's productType alone -
     * Custom.styles is never part of it there either, and is too broad for packs
     * (many different pack lines share one style label, e.g. "Baller" covers the
     * single, the 3-pack, the 5-pack, the pad-liner pack...). Their scope
     * collection (pack_swatch_scope_collection) is small and dedicated to this
     * lookup, same as the singles one, so it's fetched whole (no narrowBy) and
     * bucketed by Type locally - Type is what's precise, not any style label.
     *
     * When alt ships, replace fetchStyleSiblings with a read of product.alt and
     * delete the rest of this block.
     * ------------------------------------------------------------------ */

    const STYLE_FACET_KEY = CONFIG.swatchStyleKey || 'Custom.styles';
    const styleSiblingCache = new Map();
    let packSiblingsPromise = null;

    const productStyle = (product) => attrValue(product, STYLE_FACET_KEY);
    const productType = (product) => attrValue(product, 'Type');
    const isPackProduct = (product) => fsTags(product).includes('PACK');

    const typeKey = (product) => (productType(product) || '').toLowerCase().replace(/[^a-z0-9]/g, '');

    const SIBLING_PAGE_SIZE = 100;
    const SIBLING_MAX_PAGES = 4;

    function fetchScopedPage(categoryID, page, narrowBy) {
        return new Promise((resolve) => {
            let settled = false;
            const done = (payload) => {
                if (settled) return;
                settled = true;
                resolve(payload || {});
            };

            try {
                window.FastSimonSDK.smartCollections({
                    categoryID: categoryID,
                    page: page,
                    productsPerPage: SIBLING_PAGE_SIZE,
                    narrowBy: narrowBy,
                    facets: false,
                    withAttributes: true,
                    callback: ({ payload }) => done(payload)
                });
            } catch (e) {
                console.warn('[fs-sdk] sibling lookup failed', categoryID, narrowBy, 'page', page, e);
                done({});
            }
        });
    }

    async function fetchAllPages(categoryID, narrowBy) {
        const first = await fetchScopedPage(categoryID, 1, narrowBy);
        let products = first.products || [];
        const pages = Math.min(first.pageCount || 1, SIBLING_MAX_PAGES);

        if (pages > 1) {
            const rest = [];
            for (let page = 2; page <= pages; page++) rest.push(fetchScopedPage(categoryID, page, narrowBy));
            (await Promise.all(rest)).forEach((payload) => {
                products = products.concat(payload.products || []);
            });
        }

        // Pages can overlap when the index shifts between requests.
        const seen = new Set();
        return products.filter((product) => {
            const id = String(product.id);
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    }

    async function fetchStyleSiblings(style) {
        if (styleSiblingCache.has(style)) return styleSiblingCache.get(style);

        const categoryID = CONFIG.swatchScopeCategoryId || activeCategoryId();
        const products = await fetchAllPages(categoryID, [[STYLE_FACET_KEY, style]]);
        styleSiblingCache.set(style, products);
        return products;
    }

    function fetchPackSiblings() {
        if (!CONFIG.packSwatchScopeCategoryId) return Promise.resolve([]);
        if (!packSiblingsPromise) packSiblingsPromise = fetchAllPages(CONFIG.packSwatchScopeCategoryId, []);
        return packSiblingsPromise;
    }

    /* Type -> sibling products, deduped by id. Blank Type has nothing precise to
     * group by, so it's left out rather than falling into one merged bucket. */
    function bucketByType(products) {
        const byType = new Map();
        products.forEach((product) => {
            const type = typeKey(product);
            if (!type) return;
            if (!byType.has(type)) byType.set(type, []);
            const bucket = byType.get(type);
            if (!bucket.some((p) => String(p.id) === String(product.id))) bucket.push(product);
        });
        return byType;
    }

    async function renderSwatches() {
        const wrapper = grid();
        if (!wrapper || CONFIG.swatches === false) return;

        const cards = Array.from(wrapper.querySelectorAll('product-card'));
        const styles = new Set();
        let hasPackCard = false;

        cards.forEach((card) => {
            const product = productMap.get(card.getAttribute('product-id'));
            if (!product) return;
            if (isPackProduct(product)) {
                hasPackCard = true;
                return;
            }
            const style = productStyle(product);
            if (style) styles.add(style);
        });

        if (!styles.size && !hasPackCard) return;

        const fetches = Array.from(styles).map(fetchStyleSiblings);
        if (hasPackCard) fetches.push(fetchPackSiblings());
        await Promise.all(fetches);

        const singleByType = bucketByType(Array.from(styles).flatMap((style) => styleSiblingCache.get(style) || []));
        const packByType = hasPackCard ? bucketByType(await fetchPackSiblings()) : null;

        cards.forEach((card) => {
            const product = productMap.get(card.getAttribute('product-id'));
            if (!product) return;

            const byType = isPackProduct(product) ? packByType : singleByType;
            const siblings = (byType && byType.get(typeKey(product))) || [];
            const host = card.querySelector('.product-card__swatches');
            if (!host) return;

            host.innerHTML = '';
            // A lone colourway is not a choice - leave the row empty.
            if (siblings.length < 2) return;

            renderSwatchRow(host, orderSiblings(siblings, product.id), product);
        });
    }

    function orderSiblings(siblings, currentId) {
        const current = String(currentId);
        const ordered = siblings.slice().sort((a, b) => {
            if (String(a.id) === current) return -1;
            if (String(b.id) === current) return 1;
            return 0;
        });

        const blacks = [];
        let kept = false;

        const withoutExtraBlacks = ordered.filter((product) => {
            if ((product.l || '').toLowerCase().includes('black')) {
                if (kept) {
                    blacks.push(product);
                    return false;
                }
                kept = true;
            }
            return true;
        });

        return withoutExtraBlacks.concat(blacks);
    }

    // 5 on mobile, 6 on desktop, matching bundle-collections.js:3694 (<=769px).
    const visibleSwatchCount = () => (window.innerWidth <= 769 ? 5 : 6);

    /* Re-render when the viewport crosses the breakpoint so the visible count and
     * the +N total stay in step. Siblings are cached, so this costs no requests. */
    let lastSwatchCount = visibleSwatchCount();
    let swatchResizeTimer = null;
    window.addEventListener('resize', () => {
        clearTimeout(swatchResizeTimer);
        swatchResizeTimer = setTimeout(() => {
            const next = visibleSwatchCount();
            if (next === lastSwatchCount) return;
            lastSwatchCount = next;
            renderSwatches();
        }, 200);
    }, { passive: true });

    function renderSwatchRow(host, siblings, product) {
        const limit = visibleSwatchCount();
        const fragment = document.createDocumentFragment();

        /* Track the active swatch by the card's current selection rather than its
         * original product, so a re-render (breakpoint change) after the shopper
         * picked a colour keeps that colour marked. */
        const selectedId = host.getAttribute('data-selected-id') || String(product.id);

        siblings.slice(0, limit).forEach((sibling) => {
            productMap.set(String(sibling.id), sibling);
            const swatch = document.createElement('product-color-swatch');
            swatch.setAttribute('sibling-id', String(sibling.id));
            swatch.setAttribute('product-card-id', String(product.id));
            if (String(sibling.id) === selectedId) swatch.classList.add('active');
            fragment.appendChild(swatch);
        });

        // "+N" links through to the PDP rather than expanding in place.
        if (siblings.length > limit) {
            const plus = document.createElement('a');
            plus.className = 'product-card__swatches-plus';
            plus.href = localizedUrl(product.u);
            plus.innerText = '+' + (siblings.length - limit);
            fragment.appendChild(plus);
        }

        host.appendChild(fragment);
    }

    let warnedMissingPackIcon = false;
    function swatchImageUrl(product) {
        const icon = attrValue(product, 'custom.pack_icon');
        if (icon) return icon;

        if (!warnedMissingPackIcon) {
            warnedMissingPackIcon = true;
            console.warn('[fs-sdk] custom.pack_icon is not indexed by Fast Simon - swatches are falling back to product images.');
        }
        return product.t || product.t2 || '';
    }

   const SWATCH_STYLES = `
        .product-swatch { cursor:pointer; width:25px; height:25px; padding:0; background-color:transparent; border:none; }
        .product-swatch__image { width:100%; height:100%; background-position:center; background-size:cover;
            border-radius:79px; position:relative; border:1px solid lightgrey; }
        .product-swatch__image.pack_pd_swatch { background-size:25px; }
        .product-swatch__image:after { content:""; top:-3px; left:-3px; bottom:-3px; right:-3px; position:absolute;
            border-radius:79px; border:0.85px solid #27292c; opacity:0; transition:opacity .25s; }
        .product-swatch__image:hover:after { opacity:1; }
        :host(.active) .product-swatch__image::after { opacity:1; }
        .hide-display { display:none !important; }
        @media (max-width:768px) { .product-swatch { width:21px; height:21px; } }
    `;

    class FSProductColorSwatch extends HTMLElement {
        connectedCallback() {
            const sibling = productMap.get(this.getAttribute('sibling-id'));
            if (!sibling) return;

            const root = this.attachShadow({ mode: 'open' });
            const style = document.createElement('style');
            style.textContent = SWATCH_STYLES;

            const button = document.createElement('button');
            button.className = 'product-swatch';
            button.setAttribute('title', sibling.l || '');

            const image = document.createElement('div');
            image.className = 'product-swatch__image';

            /* Packs render the icon at a fixed 25px rather than cover, matching
             * bundle-collections.js:1584. Detected from the WSINGLES tag. */
            if (fsTags(sibling).indexOf('WSINGLES') === -1) {
                image.classList.add('pack_pd_swatch');
            }

            const src = swatchImageUrl(sibling);
            // Rendered at 25px (50px on retina) - 60 is ample.
            if (src) image.style.backgroundImage = `url("${sizedImage(src, 60)}")`;
            image.innerHTML = `<span class="hide-display">${sibling.l || ''}</span>`;

            button.appendChild(image);
            root.appendChild(style);
            root.appendChild(button);

            button.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
                swapCardToSibling(this.closest('product-card'), sibling);
            });
        }
    }

    /* Repoints an existing card at a sibling colourway without a re-fetch,
     * mirroring the in-place swap the Storefront implementation performs. */
    function swapCardToSibling(card, sibling) {
        if (!card) return;

        const originalId = card.getAttribute('product-id');
        if (originalId && String(originalId) !== String(sibling.id)) {
            reportEvent(eventName('ProductSwatchSwitchPerformed', 'productSwatchSwitch'), {
                productID: String(sibling.id),
                sourceProductID: String(originalId),
                position: parseInt(card.getAttribute('product-position'), 10) || undefined
            });
        }

        const title = sibling.l || '';
        card.querySelectorAll('.product-card__url').forEach((a) => a.setAttribute('href', localizedUrl(sibling.u)));

        const image = card.querySelector('.product-card__first-image');
        if (image) {
            // Keep the sizing - a raw URL here would undo it on every swatch click.
            applyCardImage(image, sibling, parseInt(card.getAttribute('product-position'), 10));
            image.setAttribute('alt', title);
        }

        const titleEl = card.querySelector('.product-card__title');
        if (titleEl) titleEl.innerText = title;

        const colorlessEl = card.querySelector('.product-card__title_colorless');
        if (colorlessEl) colorlessEl.innerText = colorlessTitle(sibling, title);

        // Re-run the offer rules - a sibling can carry different tags and price.
        const priceEl = card.querySelector('.product-card__price');
        if (priceEl) {
            priceEl.innerHTML = '';
            renderCardPrice(sibling, priceEl, parseFloat(sibling.p) || 0, card);
        }

        const quickAdd = card.querySelector('.product-card__atc_btn.plp_quick_add');
        if (quickAdd) quickAdd.setAttribute('data-product-id', String(sibling.id));

        card.setAttribute('product-is-pack', isPackProduct(sibling) ? 'true' : 'false');

        renderStars(sibling, card.querySelector('.okendo-star-rating'));
        applyWishlist(card, sibling, true);

        /* Move the marker only - the row order is deliberately left alone so
         * swatches do not jump under the shopper's cursor mid-selection. */
        card.querySelector('.product-card__swatches')?.setAttribute('data-selected-id', String(sibling.id));
        card.querySelectorAll('product-color-swatch').forEach((el) => {
            el.classList.toggle('active', el.getAttribute('sibling-id') === String(sibling.id));
        });
    }

    if (!customElements.get('product-color-swatch')) {
        customElements.define('product-color-swatch', FSProductColorSwatch);
    }

    /* ------------------------------------------------------------------ *
     * Boot
     * ------------------------------------------------------------------ */

    function boot() {
        if (!window.FastSimonSDK) {
            console.error('[fs-sdk] SDK global missing - check the CDN script tag.');
            return;
        }

        window.FastSimonSDK.initialization({
            storeID: CONFIG.storeID,
            uuid: CONFIG.uuid,
            // Shopify themes are full page loads, so MPA is the correct mode for reporting.
            type: 'MPA',
            onError: (error) => console.error('[fs-sdk] init failed', error),
            onReady: async () => {
                bindSorting();
                bindInfiniteScroll();
                bindFilterChrome();
                bindTopcards();
                await marketRatePromise;
                load({ resetPage: true });
            }
        });
    }

    // The SDK script is async, so it may land before or after this file.
    const SDK_SRC = 'https://assets.fastsimon.com/sdk/latest/fast_simon_sdk.js';

    function ensureSdkScript() {
        if (window.FastSimonSDK) return;
        if (document.querySelector('script[src^="https://assets.fastsimon.com/sdk/"]')) return;

        console.warn('[fs-sdk] SDK tag missing from the page - injecting it.');
        const tag = document.createElement('script');
        tag.src = SDK_SRC;
        tag.async = true;
        document.head.appendChild(tag);
    }

    function waitForSdk() {
        if (window.FastSimonSDK) {
            boot();
            return;
        }

        ensureSdkScript();

        let attempts = 0;
        const poll = setInterval(() => {
            if (window.FastSimonSDK) {
                clearInterval(poll);
                boot();
            } else if (++attempts > 100) {
                clearInterval(poll);
                console.error('[fs-sdk] SDK still unavailable after 10s -', SDK_SRC, 'did not load.');
                // Do not strand the shopper on a skeleton.
                toggleSkeleton(false);
            }
        }, 100);
    }

    waitForSdk();
})();
